// The typings the Scripts IDE hands Monaco (spec/dashboard.md section 10.8): the editor API as `huelency-editor.d.ts`
// with `Balancy` declared as the alias, and one interface per template of the branch with both casings.
import type { ContentCatalog } from '../content/ContentContext';
import type { Parameter, Template } from '../api/types';
import { pascal } from './model';

export const EDITOR_TYPINGS = `
declare namespace Huelency {
  namespace Editor {
    interface ChangedEntity { unnyId: string; id: string; templateName: string; changedParams: string[]; changes: { param: string; before: unknown; after: unknown }[]; isNew: boolean; isDeleted: boolean }
    interface PreSaveContext { changedEntities: ChangedEntity[] }
    interface PostSaveContext { idMapping: Record<string, string>; changedEntities: ChangedEntity[] }
    interface ChangeEvent { unnyId: string; id: string; templateName: string; paramName: string; newValue: unknown; oldValue: unknown }
    /** true / undefined / {} allow; { block: true, reason, entities? } blocks the named entities (every changed one when none is named) */
    type PreSaveResult = true | undefined | void | Record<string, never> | { block: true; reason: string; entities?: string[] };
    class BaseExtension {
      onInit(): Promise<void> | void;
      onDispose(): Promise<void> | void;
      preSave(ctx: PreSaveContext): Promise<PreSaveResult> | PreSaveResult;
      postSave(ctx: PostSaveContext): Promise<void> | void;
    }
    const Log: {
      info(message: unknown, data?: unknown): void;
      warn(message: unknown, data?: unknown): void;
      error(message: unknown, data?: unknown): void;
    };
  }
  /** The expanded model: $id, $unnyId, $type, $templateName and every parameter under its name and its PascalCase alias. */
  interface Model { $id: string; $unnyId: string; $type: string; $templateName: string; [field: string]: unknown }
  interface FieldDescriptor { name: string; type: string; subType?: string; refTemplate?: string; enum?: string; required: boolean; description: string | null }
  interface TemplateDescriptor { name: string; displayName: string; description: string | null; templateType: string; parentTemplate: string | null; fields: FieldDescriptor[] }
  const CMS: {
    /** a qualified name matches exactly, a short name by suffix; [] for an unknown template; heirs included unless includeChildren is false */
    getModels(templateName: string, includeChildren?: boolean): Promise<Model[]>;
    getModelByUnnyId(unnyIdOrId: string): Promise<Model | null>;
    getTemplateList(templateName?: string): Promise<TemplateDescriptor[]>;
    createModel(doc: { $type: string; [field: string]: unknown }): Promise<Model>;
    updateModel(doc: { $unnyId: string; $type?: string; [field: string]: unknown }): Promise<Model | null>;
    updateModels(docs: { $unnyId: string; $type?: string; [field: string]: unknown }[]): Promise<(Model | null)[]>;
    cloneModel(unnyIdOrId: string): Promise<Model | null>;
  };
  interface LocString { id: string; key: string; group: string; description: string | null; translations: Record<string, string>; export: boolean }
  const Localization: {
    getGroups(): Promise<{ path: string }[]>;
    getStrings(group?: string): Promise<LocString[]>;
    getStringByKey(key: string): Promise<LocString | null>;
    getLanguages(): Promise<{ code: string; name: string; localizedName: string | null; enabled: boolean }[]>;
    createGroup(path: string): Promise<{ path: string }>;
    deleteGroup(path: string): Promise<{ success: true }>;
    createString(s: { key: string; group: string; translations?: Record<string, string>; description?: string }): Promise<LocString>;
    updateString(s: { key: string; translations?: Record<string, string>; description?: string; newKey?: string; newGroup?: string }): Promise<LocString>;
    deleteString(key: string): Promise<{ success: true }>;
  };
  interface Product { id: string; itemId: string; name: string; description: string | null; type: 'Consumable' | 'NonConsumable' | 'Subscription'; price: { amount: number; currency: string }; platform: string; localizedName: string | null; localizedDescription: string | null; iconUrl: string | null }
  const Products: {
    getProducts(): Promise<Product[]>;
    getProduct(itemId: string): Promise<Product | null>;
    createProduct(p: Partial<Product> & { itemId: string }): Promise<Product>;
    updateProduct(p: Partial<Product> & { itemId: string }): Promise<Product>;
    deleteProduct(itemId: string): Promise<{ success: true }>;
  };
  interface DataObject { id: string; unnyId: string | null; name: string; type: string; ext: string; fileUrl: string | null; group: string | null }
  const Objects: {
    getObjects(type?: string): Promise<DataObject[]>;
    getObject(id: string): Promise<DataObject | null>;
    findObjectsByName(name: string): Promise<DataObject[]>;
  };
  /** Not available in Huelency: every call rejects. Use the import / export dialog. */
  const Spreadsheets: Record<string, (...args: unknown[]) => Promise<never>>;
}
/** Balancy's name for the same API: NutakuTwo's extensions run as pasted. */
declare const Balancy: typeof Huelency;
`;

function tsType(catalog: ContentCatalog, p: Parameter, depth = 0): string {
  switch (p.type) {
    case 'int':
    case 'long':
    case 'float':
    case 'duration':
    case 'date_time':
    case 'enum':
      return 'number';
    case 'bool':
      return 'boolean';
    case 'string':
    case 'localized_string':
    case 'color':
    case 'asset':
    case 'template_ref':
    case 'profile_path':
      return 'string | null';
    case 'product':
      return '{ productId: string; amount: number; currency: string } | null';
    case 'document': {
      const target = p.template ? catalog.templates.get(p.template) : undefined;
      return target && target.kind === 'component' && depth < 2 ? `${safeName(target.name)} | string | null` : 'string | null';
    }
    case 'object':
      return p.template ? `${safeName(p.template)} | null` : 'Record<string, unknown> | null';
    case 'list':
      return p.of ? `(${tsType(catalog, { ...p.of, name: p.name } as Parameter, depth + 1)})[]` : 'unknown[]';
    default:
      return 'unknown';
  }
}

export function safeName(template: string): string {
  return template.replace(/[^A-Za-z0-9_]/g, '_');
}

/** `interface Items_Sword { $id: string; … maxStack: number; MaxStack: number }` for every template of the branch (10.8). */
export function modelTypings(catalog: ContentCatalog): string {
  const out: string[] = ['// Generated from the branch schema: one interface per template, every field under both casings.'];
  for (const t of catalog.templateList as Template[]) {
    const fields = t.effectiveParameters.flatMap((p) => {
      const type = tsType(catalog, p);
      const lines = [`  ${p.name}: ${type};`];
      if (pascal(p.name) !== p.name) lines.push(`  ${pascal(p.name)}: ${type};`);
      return lines;
    });
    out.push(`/** ${t.name}${t.displayName ? ` — ${t.displayName}` : ''} */`, `interface ${safeName(t.name)} extends Huelency.Model {`, ...fields, '}');
  }
  return out.join('\n');
}
